import type { LineTableColumnDef } from '../components/LineTableColumnSettings';

export type SalesDocumentKind = 'po' | 'bill' | 'quote' | 'invoice';

export interface SalesDocumentMeta {
  kind: SalesDocumentKind;
  label: string;
  labelShort: string;
  editPhrase: string;
  documentSettingsPath: string;
  templateSettingsPath: string;
  lineStorageKey: string;
  docSettingsStorageKey: string;
  templateSettingsStoragePrefix: string;
  templates: string[];
  columns: LineTableColumnDef[];
  showSupplierCodeNote?: boolean;
}

/** FieldInsight PO “Jobs & inventory” line columns (v1.52.16). */
export const PO_LINE_COLUMN_DEFS: LineTableColumnDef[] = [
  { id: 'type', label: 'Type', locked: true, hint: 'Product or Service' },
  { id: 'category', label: 'Category' },
  { id: 'internalCode', label: 'Inventory Code', locked: true, hint: 'Your company code (FIE-234) — Auto-set mints the next' },
  { id: 'supplierCode', label: 'Supplier Code', hint: 'From supplier — only when Show Supplier Code column is ON' },
  { id: 'description', label: 'Description', locked: true },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'receivedQty', label: 'Received qty', locked: true },
  { id: 'unusedQty', label: 'Unused qty', locked: true },
  { id: 'qty', label: 'Qty', locked: true },
  { id: 'purchase', label: 'Purchase', locked: true },
  { id: 'tax', label: 'Tax' },
  { id: 'amount', label: 'Amount', locked: true },
];

export const BILL_LINE_COLUMN_DEFS: LineTableColumnDef[] = [
  { id: 'type', label: 'Type', locked: true },
  { id: 'supplierCode', label: 'Supplier Code', hint: 'From supplier — never auto-created' },
  { id: 'internalCode', label: 'Inventory Code', hint: 'Auto-set company code (FIE-234)' },
  { id: 'description', label: 'Description', locked: true },
  { id: 'qty', label: 'Qty', locked: true },
  { id: 'unitPrice', label: 'Unit Price' },
  { id: 'amount', label: 'Amount', locked: true },
];

export const QUOTE_LINE_COLUMN_DEFS: LineTableColumnDef[] = [
  { id: 'type', label: 'Type', locked: true },
  { id: 'inventoryCode', label: 'Inventory Code' },
  { id: 'description', label: 'Description', locked: true },
  { id: 'qty', label: 'Qty', locked: true },
  { id: 'purchase', label: 'Purchase' },
  { id: 'markup', label: 'Markup %' },
  { id: 'sales', label: 'Sales' },
  { id: 'discount', label: 'Disc %' },
  { id: 'tax', label: 'Tax' },
  { id: 'amount', label: 'Amount', locked: true },
];

export const INVOICE_LINE_COLUMN_DEFS: LineTableColumnDef[] = [
  { id: 'type', label: 'Type', locked: true },
  { id: 'inventoryCode', label: 'Inventory Code' },
  { id: 'description', label: 'Description', locked: true },
  { id: 'qty', label: 'Qty', locked: true },
  { id: 'purchase', label: 'Purchase' },
  { id: 'markup', label: 'Markup %' },
  { id: 'sales', label: 'Sales' },
  { id: 'discount', label: 'Disc %' },
  { id: 'tax', label: 'Tax' },
  { id: 'amount', label: 'Amount', locked: true },
];

export const SALES_DOCUMENT_META: Record<SalesDocumentKind, SalesDocumentMeta> = {
  po: {
    kind: 'po',
    label: 'Purchase Order',
    labelShort: 'PO',
    editPhrase: 'editing a Purchase Order',
    documentSettingsPath: '/settings/documents/purchase-order',
    templateSettingsPath: '/settings/templates/purchase-order',
    lineStorageKey: 'po-line-columns-v3',
    docSettingsStorageKey: 'doc-settings-po-columns-v3',
    templateSettingsStoragePrefix: 'template-settings-po',
    templates: ['Original Purchase Order', 'Service Engagement', 'Subcontractor Agreement'],
    columns: PO_LINE_COLUMN_DEFS,
    showSupplierCodeNote: true,
  },
  bill: {
    kind: 'bill',
    label: 'Bill',
    labelShort: 'Bill',
    editPhrase: 'editing a Bill',
    documentSettingsPath: '/settings/documents/bill',
    templateSettingsPath: '/settings/templates/bill',
    lineStorageKey: 'bill-line-columns-v1',
    docSettingsStorageKey: 'doc-settings-bill-columns-v1',
    templateSettingsStoragePrefix: 'template-settings-bill',
    templates: ['Default Template', 'Supplier Invoice', 'Project Bill'],
    columns: BILL_LINE_COLUMN_DEFS,
    showSupplierCodeNote: true,
  },
  quote: {
    kind: 'quote',
    label: 'Quote',
    labelShort: 'Quote',
    editPhrase: 'editing a Quote',
    documentSettingsPath: '/settings/documents/quote',
    templateSettingsPath: '/settings/templates/quote',
    lineStorageKey: 'quote-line-columns-v1',
    docSettingsStorageKey: 'doc-settings-quote-columns-v1',
    templateSettingsStoragePrefix: 'template-settings-quote',
    templates: ['Default Template', 'Defect Quote II', 'Service Quote', 'Project Quote'],
    columns: QUOTE_LINE_COLUMN_DEFS,
  },
  invoice: {
    kind: 'invoice',
    label: 'Invoice',
    labelShort: 'Invoice',
    editPhrase: 'editing an Invoice',
    documentSettingsPath: '/settings/documents/invoice',
    templateSettingsPath: '/settings/templates/invoice',
    lineStorageKey: 'invoice-line-columns-v1',
    docSettingsStorageKey: 'doc-settings-invoice-columns-v1',
    templateSettingsStoragePrefix: 'template-settings-invoice',
    templates: ['Original', 'Service Invoice', 'Project Invoice'],
    columns: INVOICE_LINE_COLUMN_DEFS,
  },
};

const KIND_BY_SLUG: Record<string, SalesDocumentKind> = {
  'purchase-order': 'po',
  bill: 'bill',
  quote: 'quote',
  invoice: 'invoice',
};

export function salesDocKindFromSlug(slug?: string): SalesDocumentKind | null {
  if (!slug) return null;
  return KIND_BY_SLUG[slug] ?? null;
}

export function templateSettingsStorageKey(meta: SalesDocumentMeta, templateName: string) {
  const slug = templateName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${meta.templateSettingsStoragePrefix}-${slug || 'default'}-columns-v1`;
}
